import { useMemo } from 'react'
import {
  Card,
  CircularProgress,
  Divider,
  List,
  Stack,
  Typography,
} from '@mui/material'
import { useGetProjectDocumentRules } from '@/service-library/hooks/project-document-rules'
import { useGetProjectGridFieldRules } from '@/service-library/hooks/project-grid-field-rules'
import { ProjectGridFieldRule } from '@/types/rules'
import { getNonGridFields, sortBySortOrder } from '@/utils/field'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import RulesList from './RulesList'
import { useWorkflowStatePageContext } from './WorkflowStatePage'

export default function WorkflowStateRules() {
  const { project, gridsMap } = useProjectContext()
  const { workflowState } = useWorkflowStatePageContext()
  const {
    projectDocumentRules,
    queryKey: docRulesQueryKey,
    isPending: docRulesIsLoading,
  } = useGetProjectDocumentRules({
    filters: {
      project_id: project.id,
      limit: '1000',
      fields__include: 'type,workflow_states_ids',
    },
  })

  const {
    projectGridFieldRules,
    queryKey: fieldRulesQueryKey,
    isPending: fieldRulesIsLoading,
  } = useGetProjectGridFieldRules({
    filters: {
      project_grid_field__project_grid__project_id: project.id,
      limit: '1000',
      fields__include: 'rule,workflow_states_ids',
    },
  })
  const fieldRulesByFieldId = useMemo(() => {
    return projectGridFieldRules.reduce<Record<string, ProjectGridFieldRule[]>>(
      (acc, rule) => {
        acc[rule.project_grid_field_id] ||= []
        acc[rule.project_grid_field_id].push(rule)
        return acc
      },
      {},
    )
  }, [projectGridFieldRules])

  const orderedGrids = useMemo(
    () => sortBySortOrder(project.project_grids || []),
    [project.project_grids],
  )

  return (
    <Stack spacing={2}>
      <Card sx={{ maxWidth: '750px' }}>
        <Typography variant="h6" sx={{ px: 2, pt: 1, pb: 0 }}>
          Document
        </Typography>
        <Divider sx={{ py: 0.5 }} />
        {docRulesIsLoading ? (
          <Stack alignItems="flex-end" sx={{ m: 1, mr: 2 }}>
            <CircularProgress size={20} />
          </Stack>
        ) : (
          <RulesList
            rules={projectDocumentRules}
            workflowStateId={workflowState.id}
            listQueryKey={docRulesQueryKey}
            showManageButton
          />
        )}
      </Card>

      {orderedGrids.map((projectGrid) => {
        const nonGridFields = sortBySortOrder(
          getNonGridFields(projectGrid.project_grid_fields),
        )
        const cardName =
          gridsMap[projectGrid.id]?.sub_project_grid_fields?.[0]?.name ||
          'Header Fields'
        return (
          <Card key={projectGrid.id} sx={{ maxWidth: '750px' }}>
            <Typography variant="h6" sx={{ px: 2, pt: 1, pb: 0 }}>
              {cardName}
            </Typography>
            {fieldRulesIsLoading ? (
              <>
                <Divider sx={{ py: 0.5 }} />
                <Stack alignItems="flex-end" sx={{ m: 1, mr: 2 }}>
                  <CircularProgress size={20} />
                </Stack>
              </>
            ) : (
              <List>
                {nonGridFields.map((field) => {
                  return (
                    <li style={{ listStyle: 'none' }} key={field.id}>
                      <RulesList
                        rules={fieldRulesByFieldId[field.id] || []}
                        listQueryKey={fieldRulesQueryKey}
                        subheaderText={field.name}
                        workflowStateId={workflowState.id}
                      />
                    </li>
                  )
                })}
              </List>
            )}
          </Card>
        )
      })}
    </Stack>
  )
}
